import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { FaSearch, FaPlus, FaExternalLinkAlt, FaStore, FaEdit, FaTrash, FaTimes } from 'react-icons/fa';
import { Loader } from '../components/Loader';
import { useAuth } from '../contexts/AuthContext';
import MainLayout from '../layouts/MainLayout';
import SEO from '../components/SEO';
import { useTheme } from '../contexts/ThemeContext';


interface Store {
  id: string;
  name: string;
  url: string;
  logo_url: string | null;
  created_at: string;
}

const StoresPage: React.FC = () => {
  const [stores, setStores] = useState<Store[]>([]);
  const [filteredStores, setFilteredStores] = useState<Store[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const { theme } = useTheme();
  
  // Estados para o modal
  const [showModal, setShowModal] = useState(false);
  const [editingStore, setEditingStore] = useState<Store | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    url: '',
    logo_url: '',
  });

  useEffect(() => {
    fetchStores();
  }, []);

  useEffect(() => {
    if (searchTerm) {
      const filtered = stores.filter(store => 
        store.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredStores(filtered);
    } else {
      setFilteredStores(stores);
    }
  }, [searchTerm, stores]);

  const fetchStores = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('stores')
        .select('*')
        .order('name');

      if (error) throw error;
      
      setStores(data || []);
      setFilteredStores(data || []);
    } catch (err) {
      console.error('Erro ao buscar lojas:', err);
      setError('Não foi possível carregar a lista de lojas.');
    } finally {
      setLoading(false);
    }
  };

  const handleStoreClick = (storeId: string) => {
    navigate(`/stores/${storeId}/products`);
  };

  const handleAddStore = () => {
    // Resetar o formulário para valores padrão
    setFormData({
      name: '',
      url: '',
      logo_url: '',
    });
    setEditingStore(null);
    setShowModal(true);
  };

  const handleEditStore = (e: React.MouseEvent, store: Store) => {
    e.stopPropagation();
    // Preencher formulário com os dados da loja
    setFormData({
      name: store.name,
      url: store.url,
      logo_url: store.logo_url || '',
    });
    setEditingStore(store);
    setShowModal(true);
  };

  const handleDeleteStore = async (e: React.MouseEvent, storeId: string) => {
    e.stopPropagation();
    if (window.confirm('Tem certeza que deseja excluir esta loja?')) {
      try {
        const { error } = await supabase
          .from('stores')
          .delete()
          .eq('id', storeId);

        if (error) throw error;
        
        // Atualizar lista após exclusão
        setStores(stores.filter(store => store.id !== storeId));
      } catch (err) {
        console.error('Erro ao excluir loja:', err);
        alert('Não foi possível excluir a loja.');
      }
    }
  };

  const visitStore = (e: React.MouseEvent, url: string) => {
    e.stopPropagation();
    window.open(url, '_blank');
  };
  
  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      if (editingStore) {
        // Atualizar loja existente
        const { error } = await supabase
          .from('stores')
          .update({
            name: formData.name,
            url: formData.url,
            logo_url: formData.logo_url || null,
          })
          .eq('id', editingStore.id);
          
        if (error) throw error;
        
        // Atualizar a lista de lojas
        setStores(stores.map(store => 
          store.id === editingStore.id 
            ? { ...store, ...formData } 
            : store
        ));
        
        alert('Loja atualizada com sucesso!');
      } else {
        // Adicionar nova loja
        const { data, error } = await supabase
          .from('stores')
          .insert({
            name: formData.name,
            url: formData.url,
            logo_url: formData.logo_url || null,
          })
          .select();
          
        if (error) throw error;
        
        // Adicionar a nova loja à lista
        if (data && data.length > 0) {
          setStores([...stores, data[0]]);
        }
        
        alert('Loja adicionada com sucesso!');
      }
      
      // Fechar o modal e limpar o formulário
      setShowModal(false);
      setEditingStore(null);
      
    } catch (err) {
      console.error('Erro ao salvar loja:', err);
      alert('Erro ao salvar loja. Verifique os dados e tente novamente.');
    }
  };

  return (
    <><SEO
      title="Lojas Parceiras"
      description="Encontre jogos de tabuleiro para sua coleção usando nossa ferramenta de busca." /><MainLayout>
        <div className={`px-4 py-6 max-w-6xl mx-auto ${theme === 'dark' ? 'text-foreground' : 'bg-gray-50 text-gray-900'}`}>
          <div className="flex justify-between items-center mb-6">
            <h1 className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-800'}`}>Lojas Parceiras</h1>
            {isAdmin && (
              <button
                onClick={handleAddStore}
                className={`bg-indigo-600 text-white px-4 py-2 rounded-lg flex items-center hover:bg-indigo-700 ${theme === 'dark' ? 'dark:bg-indigo-500 dark:hover:bg-indigo-600' : ''}`}
              >
                <FaPlus className="mr-2" /> Adicionar Loja
              </button>
            )}
          </div>

          <div className="relative mb-6">
            <input
              type="text"
              placeholder="Buscar lojas..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`w-full px-4 py-2 pl-10 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 ${theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:ring-indigo-400' : 'border-gray-300 text-gray-900 placeholder-gray-500'}`} />
            <FaSearch className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-400'}`} />
          </div>

          {loading ? (
            <div className="flex justify-center my-12">
              <Loader />
            </div>
          ) : error ? (
            <div className="text-center text-red-500 my-12">{error}</div>
          ) : filteredStores.length === 0 ? (
            <div className={`text-center my-12 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
              {searchTerm ? 'Nenhuma loja encontrada com este termo.' : 'Nenhuma loja cadastrada.'}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredStores.map((store) => (
                <div
                  key={store.id}
                  onClick={() => handleStoreClick(store.id)}
                  className={`rounded-lg shadow p-4 cursor-pointer transition-all hover:shadow-md ${theme === 'dark' ? 'bg-gray-800 hover:shadow-gray-700/50' : 'bg-white'}`}
                >
                  <div className="flex items-center mb-3">
                    <div className={`w-12 h-12 rounded-full overflow-hidden flex items-center justify-center ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-100'}`}>
                      {store.logo_url ? (
                        <img
                          src={store.logo_url}
                          alt={store.name}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = 'https://via.placeholder.com/100?text=Logo';
                          } } />
                      ) : (
                        <FaStore className={`${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'} text-xl`} />
                      )}
                    </div>
                    <div className="ml-3 flex-grow">
                      <h3 className={`font-semibold text-lg ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{store.name}</h3>
                    </div>
                    <button
                      onClick={(e) => visitStore(e, store.url)}
                      className={`${theme === 'dark' ? 'text-gray-400 hover:text-indigo-400' : 'text-gray-500 hover:text-indigo-600'}`}
                      title="Visitar loja"
                    >
                      <FaExternalLinkAlt />
                    </button>
                  </div>

                  {isAdmin && (
                    <div className="flex justify-end mt-2 space-x-2">
                      <button
                        onClick={(e) => handleEditStore(e, store)}
                        className={`text-blue-500 hover:text-blue-700 ${theme === 'dark' ? 'text-blue-400 hover:text-blue-300' : ''}`}
                        title="Editar loja"
                      >
                        <FaEdit />
                      </button>
                      <button
                        onClick={(e) => handleDeleteStore(e, store.id)}
                        className={`text-red-500 hover:text-red-700 ${theme === 'dark' ? 'text-red-400 hover:text-red-300' : ''}`}
                        title="Excluir loja"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Modal para adicionar/editar loja */}
          {showModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className={`rounded-lg shadow-xl w-full max-w-md ${theme === 'dark' ? 'bg-gray-800 text-gray-100' : 'bg-white text-gray-900'}`}>
                <div className={`flex justify-between items-center p-5 ${theme === 'dark' ? 'border-b border-gray-700' : 'border-b'}`}>
                  <h3 className="text-lg font-semibold">
                    {editingStore ? 'Editar Loja' : 'Adicionar Nova Loja'}
                  </h3>
                  <button
                    onClick={() => setShowModal(false)}
                    className={`${theme === 'dark' ? 'text-gray-400 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'}`}
                  >
                    <FaTimes size={20} />
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="p-5">
                  <div className="mb-4">
                    <label className={`block text-sm font-medium mb-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                      Nome da Loja*
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleFormChange}
                      className={`w-full px-3 py-2 border rounded-md ${theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white focus:ring-indigo-400' : 'border-gray-300 focus:ring-indigo-500'}`}
                      required />
                  </div>

                  <div className="mb-4">
                    <label className={`block text-sm font-medium mb-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                      URL da Loja*
                    </label>
                    <input
                      type="url"
                      name="url"
                      value={formData.url}
                      onChange={handleFormChange}
                      className={`w-full px-3 py-2 border rounded-md ${theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white focus:ring-indigo-400' : 'border-gray-300 focus:ring-indigo-500'}`}
                      required />
                  </div>

                  <div className="mb-4">
                    <label className={`block text-sm font-medium mb-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                      URL do Logo
                    </label>
                    <input
                      type="url"
                      name="logo_url"
                      value={formData.logo_url}
                      onChange={handleFormChange}
                      className={`w-full px-3 py-2 border rounded-md ${theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:ring-indigo-400' : 'border-gray-300 placeholder-gray-500 focus:ring-indigo-500'}`}
                      placeholder="https://example.com/logo.png" />
                  </div>

                  <div className="flex justify-end space-x-2">
                    <button
                      type="button"
                      onClick={() => setShowModal(false)}
                      className={`px-4 py-2 rounded-md ${theme === 'dark' ? 'bg-gray-600 text-gray-200 hover:bg-gray-500' : 'bg-gray-200 text-gray-800 hover:bg-gray-300'}`}
                    >
                      Cancelar
                    </button>
                    <button
                      type="submit"
                      className={`px-4 py-2 rounded-md hover:bg-indigo-700 ${theme === 'dark' ? 'bg-indigo-500 text-white hover:bg-indigo-600' : 'bg-indigo-600 text-white'}`}
                    >
                      {editingStore ? 'Atualizar' : 'Adicionar'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      </MainLayout></>
  );
};

export default StoresPage; 